/**
 * The setup-file form's state and what it sends (docs/GatewaySetupFile.md).
 *
 * The form keeps text as typed; `toSettings` turns it into the backend's
 * `SetupSettings`, leaving out whatever was left blank or switched off: an
 * absent setting is one the gateway leaves alone. The backend validates with
 * the gateway's own rules and answers with a list of problems.
 */

export interface InterfaceForm {
  /** Addresses with prefix lengths, separated by commas or spaces. */
  addresses: string;
  dhcp4: boolean;
  dhcp6: boolean;
  acceptRa: boolean;
  gateway4: string;
  gateway6: string;
  mtu: string;
}

export interface EthernetForm extends InterfaceForm {
  key: string;
  /** The VyOS port name (eth0), or a name of the file's own when matched by MAC. */
  name: string;
  macaddress: string;
}

export interface VlanForm extends InterfaceForm {
  key: string;
  /** Blank: `<link>.<id>`. */
  name: string;
  id: string;
  /** The name of one of the form's ports. */
  link: string;
}

export interface RouteForm {
  key: string;
  to: string;
  via: string;
}

export interface SetupForm {
  hostname: string;
  /** An interface name, or '' for none. */
  uplink: string;
  ethernets: EthernetForm[];
  vlans: VlanForm[];
  setNameservers: boolean;
  nameservers: string;
  setSearch: boolean;
  search: string;
  setNtp: boolean;
  ntp: string;
  setRoutes: boolean;
  routes: RouteForm[];
  setProxy: boolean;
  /** host:port; blank is no proxy. */
  proxyAddress: string;
  proxyUser: string;
  proxyPassword: string;
  /** PEM: the CAs a proxy or firewall that inspects TLS signs with; blank is none. */
  proxyCa: string;
}

interface InterfaceSettings {
  addresses?: string[];
  dhcp4?: boolean;
  dhcp6?: boolean;
  acceptRa?: boolean;
  gateway4?: string;
  gateway6?: string;
  mtu?: number;
}

/** The backend's `SetupSettings`. */
export interface SetupSettings {
  hostname?: string;
  uplink?: string;
  ethernets?: { name: string; macaddress?: string; settings: InterfaceSettings }[];
  vlans?: { name: string; id: number; link: string; settings: InterfaceSettings }[];
  routes?: { to: string; via: string }[];
  nameservers?: { addresses?: string[]; search?: string[] };
  ntp?: string[];
  /** Replaces the gateway's whole setting. No `url`: no proxy; no `caCertificates`: the public CAs alone. */
  proxy?: { url?: string; caCertificates?: string };
}

export interface SetupFileResponse {
  fileName: string;
  yaml: string;
  signatureName: string;
  signature: string;
  issuedAt: string;
  key: string;
}

let nextKey = 1;
/** A row's React key: rows are added and removed, so not their index. */
export function newKey(): string {
  return `row-${String(nextKey++)}`;
}

export function blankInterface(): InterfaceForm {
  return { addresses: '', dhcp4: false, dhcp6: false, acceptRa: false, gateway4: '', gateway6: '', mtu: '' };
}

export function blankEthernet(name = ''): EthernetForm {
  return { key: newKey(), name, macaddress: '', ...blankInterface() };
}

export function blankVlan(link = ''): VlanForm {
  return { key: newKey(), name: '', id: '', link, ...blankInterface() };
}

/**
 * Where a new form starts: empty. The file replaces what it lists, so a port
 * filled in by default would be reset by a file meant only to rename the host.
 */
export function initialForm(): SetupForm {
  return {
    hostname: '',
    uplink: '',
    ethernets: [],
    vlans: [],
    setNameservers: false,
    nameservers: '',
    setSearch: false,
    search: '',
    setNtp: false,
    ntp: '',
    setRoutes: false,
    routes: [],
    setProxy: false,
    proxyAddress: '',
    proxyUser: '',
    proxyPassword: '',
    proxyCa: '',
  };
}

/** Words separated by commas, spaces or new lines. */
export function words(text: string): string[] {
  return text.split(/[\s,]+/).filter((w) => w !== '');
}

export function vlanName(v: VlanForm): string {
  return v.name.trim() || `${v.link}.${v.id.trim()}`;
}

/** Every interface the uplink can be. */
export function interfaceNames(form: SetupForm): string[] {
  return [
    ...form.ethernets.map((e) => e.name.trim()).filter((n) => n !== ''),
    ...form.vlans.filter((v) => v.link && v.id.trim()).map(vlanName),
  ];
}

function interfaceSettings(i: InterfaceForm): InterfaceSettings {
  const s: InterfaceSettings = {};
  const addresses = words(i.addresses);
  if (addresses.length > 0) s.addresses = addresses;
  if (i.dhcp4) s.dhcp4 = true;
  if (i.dhcp6) s.dhcp6 = true;
  if (i.acceptRa) s.acceptRa = true;
  if (i.gateway4.trim()) s.gateway4 = i.gateway4.trim();
  if (i.gateway6.trim()) s.gateway6 = i.gateway6.trim();
  // A non-number is sent as one the backend refuses, so the problem is reported, not dropped.
  if (i.mtu.trim()) s.mtu = /^\d+$/.test(i.mtu.trim()) ? Number(i.mtu.trim()) : -1;
  return s;
}

export function toSettings(form: SetupForm): SetupSettings {
  const s: SetupSettings = {};
  if (form.hostname.trim()) s.hostname = form.hostname.trim();
  if (form.uplink) s.uplink = form.uplink;
  if (form.ethernets.length > 0) {
    s.ethernets = form.ethernets.map((e) => ({
      name: e.name.trim(),
      ...(e.macaddress.trim() ? { macaddress: e.macaddress.trim() } : {}),
      settings: interfaceSettings(e),
    }));
  }
  if (form.vlans.length > 0) {
    s.vlans = form.vlans.map((v) => ({
      name: vlanName(v),
      id: /^\d+$/.test(v.id.trim()) ? Number(v.id.trim()) : 0,
      link: v.link,
      settings: interfaceSettings(v),
    }));
  }
  if (form.setNameservers || form.setSearch) {
    s.nameservers = {
      ...(form.setNameservers ? { addresses: words(form.nameservers) } : {}),
      ...(form.setSearch ? { search: words(form.search) } : {}),
    };
  }
  if (form.setNtp) s.ntp = words(form.ntp);
  if (form.setRoutes) s.routes = form.routes.map((r) => ({ to: r.to.trim(), via: r.via.trim() }));
  if (form.setProxy) {
    const url = proxyUrl(form);
    s.proxy = {
      ...(url === undefined ? {} : { url }),
      ...(form.proxyCa.trim() ? { caCertificates: form.proxyCa } : {}),
    };
  }
  return s;
}

/**
 * The proxy's URL from its fields: `http://` unless the address names a scheme
 * (which the backend then judges), and the credentials escaped, so a password
 * may hold any character. Undefined: no proxy.
 */
export function proxyUrl(form: SetupForm): string | undefined {
  const address = form.proxyAddress.trim();
  if (!address) return undefined;
  const at = address.indexOf('://');
  const scheme = at < 0 ? 'http://' : address.slice(0, at + 3);
  const rest = at < 0 ? address : address.slice(at + 3);
  const { proxyUser: user, proxyPassword: password } = form;
  const credentials = user || password ? `${escape(user)}${password ? `:${escape(password)}` : ''}@` : '';
  return `${scheme}${credentials}${rest}`;
}

/** Everything but RFC 3986's unreserved characters, %-escaped as UTF-8. */
function escape(text: string): string {
  return Array.from(new TextEncoder().encode(text), (b) => {
    const c = String.fromCharCode(b);
    return /[A-Za-z0-9._~-]/.test(c) ? c : `%${b.toString(16).toUpperCase().padStart(2, '0')}`;
  }).join('');
}

/** The problems a 422 lists, if that is what came back. */
export function problemsOf(body: unknown): string[] | undefined {
  if (body && typeof body === 'object' && 'problems' in body) {
    const problems = body.problems;
    if (Array.isArray(problems) && problems.every((p) => typeof p === 'string')) return problems;
  }
  return undefined;
}

/** Hand the browser a file to save. */
export function saveFile(name: string, data: Blob): void {
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Deferred: revoking in the click's own tick can cancel a download not yet started.
  setTimeout(() => { URL.revokeObjectURL(url); }, 0);
}
